import React, { useEffect, useState } from "react";
import "./App.css";
import profileImage from "./Images/majid.png";
import cucumberImage from "./Images/selenium-cucumber.png";
import testNgImage from "./Images/Selenium-testng-java.png";
import junitImage from "./Images/Junit-selenium-docker.jpg";
import csharpImage from "./Images/selenium-C#.png";
import apiImage from "./Images/rest-assured.png";

const projects = [
  {
    title: "AI-assisted Playwright Framework",
    eyebrow: "Playwright · TypeScript · AI workflow",
    description: "A Playwright-first SDET framework with typed page objects, smoke and regression suites, cross-browser execution, rich reports, and optional AI-assisted QA workflows.",
    tags: ["Page objects", "GitLab CI", "Allure"],
    art: "playwright",
    link: "https://gitlab.com/mmajid.automation/playwright-ai-sdet-framework",
  },
  {
    title: "Postman API Automation",
    eyebrow: "Postman · Newman · GitLab CI",
    description: "A repeatable Bookstore API testing project built with Postman collections, environment configuration, Newman execution, and GitLab pipeline automation.",
    tags: ["API testing", "Newman", "CI/CD"],
    art: "postman",
    link: "https://gitlab.com/mmajid.automation/postman-api-automation-gitlab",
  },
  {
    title: "BDD Web Automation",
    eyebrow: "Java · Selenium · Cucumber",
    description: "A behavior-driven UI automation framework that turns product scenarios into readable, reusable test coverage.",
    tags: ["Page objects", "Gherkin", "Maven"],
    image: cucumberImage,
    link: "https://bitbucket.org/mohammadnafiulmajid/abletoautomation/src/master/",
  },
  {
    title: "Scalable Regression Suite",
    eyebrow: "Java · Selenium · TestNG",
    description: "A maintainable browser-testing foundation designed around reusable components, clear assertions, and repeatable execution.",
    tags: ["TestNG", "Regression", "Data driven"],
    image: testNgImage,
    link: "https://bitbucket.org/mohammadnafiulmajid/pageobjectwithtestng/src/master/",
  },
  {
    title: "API Quality Framework",
    eyebrow: "REST Assured · Java · API",
    description: "Automated API checks for validating contracts, response data, status codes, and critical service behavior.",
    tags: ["REST Assured", "JSON", "Integration"],
    image: apiImage,
    link: "https://gitlab.com/mmajid.automation/bookstoreapi-rest-assured",
  },
  {
    title: "Cross-stack UI Testing",
    eyebrow: "C# · Selenium · NUnit",
    description: "A .NET-based automation framework showing the same quality-first principles across a different engineering stack.",
    tags: ["NUnit", ".NET", "Selenium"],
    image: csharpImage,
    link: "https://bitbucket.org/mohammadnafiulmajid/seleniumwithcsharp.net/src/master/",
  },
  {
    title: "Container-ready Testing",
    eyebrow: "Java · JUnit · Docker",
    description: "A portable UI-testing setup built to make test execution more consistent across local and delivery environments.",
    tags: ["JUnit", "Docker", "Automation"],
    image: junitImage,
  },
];

const skillGroups = [
  {
    number: "01",
    title: "Test automation",
    copy: "Reliable frameworks that keep pace with product development and make failures easier to diagnose.",
    skills: ["Selenium", "Cucumber", "TestNG", "JUnit", "NUnit"],
  },
  {
    number: "02",
    title: "API & data",
    copy: "Service-level validation that catches integration problems before they reach the interface.",
    skills: ["REST Assured", "Postman", "SQL", "MongoDB", "JSON"],
  },
  {
    number: "03",
    title: "Engineering workflow",
    copy: "Quality practices designed to fit naturally into modern software delivery teams.",
    skills: ["Java", "C#", "JavaScript", "Git", "Jenkins", "Maven"],
  },
];

const process = [
  { step: "Understand risk", text: "Translate product behavior and edge cases into a focused quality strategy." },
  { step: "Design coverage", text: "Balance UI, API, integration, and exploratory testing at the right layers." },
  { step: "Automate wisely", text: "Build readable, reusable checks that provide fast and trustworthy feedback." },
  { step: "Improve continuously", text: "Use failures, defect patterns, and team feedback to strengthen the process." },
];

function ArrowIcon() {
  return <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h11M10.5 5.5 15 10l-4.5 4.5" /></svg>;
}

function ExternalIcon() {
  return <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M11 4h5v5M9 11l7-7M16 11v4a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h4" /></svg>;
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const closeMenu = () => setMenuOpen(false);
    window.addEventListener("resize", closeMenu);
    return () => window.removeEventListener("resize", closeMenu);
  }, []);

  return (
    <div className="site-shell">
      <header className="site-header">
        <a className="brand" href="#top" aria-label="Mohammad Majid, home">
          <span className="brand-mark">MM</span>
          <span className="brand-name">Mohammad Majid</span>
        </a>

        <button className="menu-button" type="button" aria-label="Toggle navigation" aria-expanded={menuOpen} onClick={() => setMenuOpen((open) => !open)}>
          <span /><span />
        </button>

        <nav className={menuOpen ? "site-nav is-open" : "site-nav"} aria-label="Main navigation">
          <a href="#work" onClick={() => setMenuOpen(false)}>Work</a>
          <a href="#expertise" onClick={() => setMenuOpen(false)}>Expertise</a>
          <a href="#approach" onClick={() => setMenuOpen(false)}>Approach</a>
          <a className="nav-cta" href="#contact" onClick={() => setMenuOpen(false)}>Let&apos;s talk <ArrowIcon /></a>
        </nav>
      </header>

      <main id="top">
        <section className="hero section-wrap" aria-labelledby="hero-heading">
          <div className="hero-copy">
            <div className="eyebrow"><span /> Software QA Engineer · SDET</div>
            <h1 id="hero-heading">I build confidence<br />into <em>every release.</em></h1>
            <p className="hero-lede">I&apos;m Mohammad, an automation-focused QA engineer creating dependable test systems for web applications, APIs, and modern delivery pipelines.</p>
            <div className="hero-actions">
              <a className="button button-primary" href="#work">Explore my work <ArrowIcon /></a>
              <a className="text-link" href="mailto:mohammadnafiulmajid@gmail.com">mohammadnafiulmajid@gmail.com</a>
            </div>
            <dl className="hero-stats" aria-label="Professional focus">
              <div><dt>07</dt><dd>QA engineering projects</dd></div>
              <div><dt>UI + API</dt><dd>Full-stack quality focus</dd></div>
              <div><dt>Java / C#</dt><dd>Cross-stack engineering</dd></div>
            </dl>
          </div>

          <div className="hero-visual" aria-label="Mohammad Majid, Software QA Engineer">
            <div className="portrait-frame">
              <div className="portrait-grid" />
              <img src={profileImage} alt="Mohammad Majid" />
              <div className="status-card"><span className="status-dot" /><span><strong>Open to opportunities</strong>Software QA · SDET</span></div>
            </div>
          </div>
        </section>

        <div className="marquee" aria-hidden="true"><div>
          <span>Automation</span><i>✦</i><span>API Testing</span><i>✦</i><span>Quality Strategy</span><i>✦</i><span>CI/CD</span><i>✦</i><span>Exploratory Testing</span>
        </div></div>

        <section className="work-section section-wrap" id="work" aria-labelledby="work-heading">
          <div className="section-heading split-heading">
            <div><span className="section-kicker">Selected work</span><h2 id="work-heading">Frameworks built for<br />real-world quality.</h2></div>
            <p>Practical automation projects across browser, API, Java, and .NET ecosystems.</p>
          </div>

          <div className="project-grid">
            {projects.map((project, index) => {
              const content = <>
                <div className="project-image-wrap">
                  {project.image ? <img src={project.image} alt="" /> : (
                    <div className={`project-art project-art--${project.art}`} aria-hidden="true">
                      <span className="art-grid" />
                      <span className="art-orbit" />
                      <strong>{project.art === "playwright" ? "PW" : "API"}</strong>
                      <span className="art-label">{project.art === "playwright" ? "TypeScript / E2E" : "Postman / Newman"}</span>
                      <span className="art-status">✓ pipeline passed</span>
                    </div>
                  )}
                  <span className="project-number">0{index + 1}</span>
                  {project.link && <span className="project-open"><ExternalIcon /></span>}
                </div>
                <div className="project-content">
                  <span className="project-eyebrow">{project.eyebrow}</span>
                  <h3>{project.title}</h3><p>{project.description}</p>
                  <ul aria-label={`${project.title} technologies`}>{project.tags.map((tag) => <li key={tag}>{tag}</li>)}</ul>
                </div>
              </>;
              return project.link ? (
                <a className="project-card" href={project.link} target="_blank" rel="noreferrer" key={project.title}>{content}</a>
              ) : <article className="project-card" key={project.title}>{content}</article>;
            })}
          </div>
        </section>

        <section className="expertise-section" id="expertise" aria-labelledby="expertise-heading">
          <div className="section-wrap">
            <div className="section-heading split-heading light-heading">
              <div><span className="section-kicker">Core expertise</span><h2 id="expertise-heading">Quality is an engineering<br />discipline.</h2></div>
              <p>I combine technical depth with a product mindset to help teams ship with fewer surprises.</p>
            </div>
            <div className="skill-grid">
              {skillGroups.map((group) => <article className="skill-card" key={group.title}>
                <span className="skill-number">{group.number}</span><h3>{group.title}</h3><p>{group.copy}</p>
                <ul>{group.skills.map((skill) => <li key={skill}>{skill}</li>)}</ul>
              </article>)}
            </div>
          </div>
        </section>

        <section className="approach-section section-wrap" id="approach" aria-labelledby="approach-heading">
          <div className="approach-intro">
            <span className="section-kicker">How I work</span><h2 id="approach-heading">From requirement<br />to reliable release.</h2>
            <p>Good testing is more than finding bugs. It creates fast feedback, shared understanding, and a safer path to production.</p>
          </div>
          <ol className="process-list">
            {process.map((item, index) => <li key={item.step}><span>0{index + 1}</span><div><h3>{item.step}</h3><p>{item.text}</p></div></li>)}
          </ol>
        </section>

        <section className="contact-section" id="contact" aria-labelledby="contact-heading">
          <div className="section-wrap contact-inner">
            <div><span className="section-kicker">Let&apos;s build better software</span><h2 id="contact-heading">Have a quality challenge?<br /><em>Let&apos;s solve it.</em></h2></div>
            <div className="contact-actions">
              <a className="button button-light" href="mailto:mohammadnafiulmajid@gmail.com">Start a conversation <ArrowIcon /></a>
              <div className="social-links">
                <a href="https://www.linkedin.com/in/mohammad-majid" target="_blank" rel="noreferrer">LinkedIn <ExternalIcon /></a>
                <a href="https://github.com/MohammadMajid" target="_blank" rel="noreferrer">GitHub <ExternalIcon /></a>
                <a href="https://gitlab.com/users/mmajid.automation/projects" target="_blank" rel="noreferrer">GitLab <ExternalIcon /></a>
                <a href="https://bitbucket.org/mohammadnafiulmajid/" target="_blank" rel="noreferrer">Bitbucket <ExternalIcon /></a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer"><div className="section-wrap">
        <span>© {new Date().getFullYear()} Mohammad Majid</span><span>Software QA Engineer · SDET</span><a href="#top">Back to top ↑</a>
      </div></footer>
    </div>
  );
}

export default App;
